import { position, debounce, requireContext } from ".";
import { Accessor, createContext, createEffect, createSignal, Setter, Signal, useContext } from "solid-js";

export type TriggerAction = 'click' | 'hover'

let activeDropdown: DropdownContext | undefined = undefined

export class DropdownContext {
    isOpen: Accessor<boolean>; setIsOpen: Setter<boolean>;
    offsetX: Accessor<number>; private setOffsetX: Setter<number>;
    offsetY: Accessor<number>; private setOffsetY: Setter<number>;

    isHoveringTrigger: Accessor<boolean>; setIsHoveringTrigger: Setter<boolean>;
    isHoveringContent: Accessor<boolean>; setIsHoveringContent: Setter<boolean>;
    placement: Accessor<position.Placement>; setPlacement: Setter<position.Placement>;
    triggerAction: Accessor<TriggerAction>; setTriggerAction: Setter<TriggerAction>;

    trigger: HTMLElement | undefined;
    content: HTMLElement | undefined;

    constructor(open?: Signal<boolean>, triggerAction?: TriggerAction) {
        [this.isOpen, this.setIsOpen] = open ?? createSignal(false);
        [this.offsetX, this.setOffsetX] = createSignal(0);
        [this.offsetY, this.setOffsetY] = createSignal(0);
        [this.placement, this.setPlacement] = createSignal<position.Placement>('bottom-start');
        [this.isHoveringTrigger, this.setIsHoveringTrigger] = createSignal(false);
        [this.isHoveringContent, this.setIsHoveringContent] = createSignal(false);
        [this.triggerAction, this.setTriggerAction] = createSignal<TriggerAction>(triggerAction ?? 'click');

        createEffect(() => {
            handleHoverChange(this.triggerAction(), this.isHoveringTrigger(), this.isHoveringContent());
        })

        const handleHoverChange = debounce((action: TriggerAction, hoveringTrigger: boolean, hoveringContent: boolean) => {
            if (action === 'hover') {
                if (hoveringTrigger || hoveringContent) {
                    this.setIsOpen(true)
                } else {
                    this.setIsOpen(false)
                }
            }
        }, 400)
    }

    toggle(state?: boolean) {
        this.setIsOpen(state ?? !this.isOpen())
    }

    registerTrigger(trigger: HTMLElement) {
        this.trigger = trigger
        this.resetWatcher()
    }

    registerContent(content: HTMLElement) {
        this.content = content
        this.resetWatcher() 
    }


    cleanupWatcher: () => void = () => { }
    resetWatcher() {
        requestAnimationFrame(() => {
            this.cleanupWatcher()
            if (this.trigger && this.content) {
                this.cleanupWatcher = position.observePosition(this.trigger!, this.content!, () => {
                    const { x, y } = position.computePosition(this.trigger!, this.content!, { placement: this.placement() })
                    this.setOffsetX(x)
                    this.setOffsetY(y)
                })
            }

        })
    }

    
    reposition(placement: position.Placement) {
        if (!this.trigger || !this.content) return;

        const { x, y } = position.computePosition(this.trigger, this.content, { placement })
        this.setOffsetX(x)
        this.setOffsetY(y)
    }

}

const DropdownContextComponent = createContext<DropdownContext>();

export const DropdownProvider = DropdownContextComponent.Provider

export const requireDropdownContext = () => {
    return requireContext(DropdownContextComponent);
}

export const useDropdownContext = () => useContext(DropdownContextComponent);
